import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import WakeUpNumberCard from '../components/WakeUpNumberCard'
import { CALL_STATUSES, APP_CONFIG } from '../data/constants.jsx'
import { formatBengaliTime } from '../utils/timeHelpers'
import apiService from '../services/api'

const CommunityWakeUp = () => {
  return (
    <AuthGuard 
      requireAuth={true}
      title="কমিউনিটি জাগানোর সেবা ব্যবহার করতে লগিন করুন"
      description="আপনার কমিউনিটির সদস্যদের জাগানোর সেবা ব্যবহার করতে অ্যাকাউন্টে লগিন করুন।"
    >
      {({ user, logout, openAuth }) => <CommunityWakeUpContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const CommunityWakeUpContent = ({ user, logout, openAuth }) => {
  const navigate = useNavigate()
  const [communities, setCommunities] = useState([])
  const [selectedCommunity, setSelectedCommunity] = useState(null)
  const [availableMembers, setAvailableMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  
  // Wake-up system states
  const [currentNumber, setCurrentNumber] = useState(null)
  const [trackingId, setTrackingId] = useState(null)
  const [remainingCalls, setRemainingCalls] = useState(0)
  const [canRequestNext, setCanRequestNext] = useState(false)
  const [wakeUpLoading, setWakeUpLoading] = useState(false)
  
  // Prevent duplicate API calls
  const activeRequestRef = useRef(false)
  
  useEffect(() => {
    loadCommunities()
  }, [])

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    
    return () => clearInterval(timer)
  }, [])

  const loadCommunities = async () => {
    try {
      setLoading(true)
      const response = await apiService.getUserCommunities()
      const communitiesList = response.communities || []
      setCommunities(communitiesList)
      
      // If user only has one community, select it automatically
      if (communitiesList.length === 1) {
        await selectCommunityWithData(communitiesList[0].id, communitiesList)
      } else {
        setLoading(false)
      }
    } catch (err) {
      console.error('Failed to load communities:', err)
      setError(err.message)
      setLoading(false)
    }
  }

  const selectCommunity = async (communityId) => {
    await selectCommunityWithData(communityId, communities)
  }

  const selectCommunityWithData = async (communityId, communitiesList) => {
    try {
      setLoading(true)
      const community = communitiesList.find(c => c.id === communityId)
      
      if (!community) {
        setError(`Community with ID ${communityId} not found`)
        return
      }
      
      setSelectedCommunity(community)
      
      // Load available members
      const response = await apiService.getCommunityAvailableMembers(communityId)
      setAvailableMembers(response.available_members || [])
      
      // Get first number from wake-up system - pass community directly
      await getNextCommunityNumberForCommunity(community)
    } catch (err) {
      console.error('Failed to load community members:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const getNextCommunityNumberForCommunity = async (community) => {
    // Prevent duplicate calls
    if (activeRequestRef.current) {
      console.log('🚫 Preventing duplicate API call - request already in progress')
      return
    }
    
    try {
      activeRequestRef.current = true
      setWakeUpLoading(true)
      
      // Request a number (backend will check for pending first)
      const result = await apiService.getCommunityNumber(community.id)
      
      if (result.number) {
        setCurrentNumber(result.number)
        setTrackingId(result.tracking_id)
        setRemainingCalls(5) // Set reasonable default
        setCanRequestNext(true) // Always allow next for community
        setError(null)
      } else {
        setCurrentNumber(null)
        // Don't show error if no numbers available - this is expected
        if (!result.message.includes('No available')) {
          setError(result.message || 'কোন কমিউনিটি সদস্য পাওয়া যায়নি')
        }
      }
    } catch (err) {
      console.error('Failed to get community number:', err)
      setError(err.message)
    } finally {
      setWakeUpLoading(false)
      activeRequestRef.current = false
    }
  }

  const getNextCommunityNumber = async () => {
    try {
      setWakeUpLoading(true)
      
      // Check if community is selected
      if (!selectedCommunity) {
        setError('No community selected')
        return
      }
      
      // Request a number (backend will check for pending first)
      const result = await apiService.getCommunityNumber(selectedCommunity.id)
      
      if (result.number) {
        setCurrentNumber(result.number)
        setTrackingId(result.tracking_id)
        setRemainingCalls(5) // Set reasonable default
        setCanRequestNext(true) // Always allow next for community
        setError(null)
      } else {
        setCurrentNumber(null)
        // Don't show error if no numbers available - this is expected
        if (!result.message.includes('No available')) {
          setError(result.message || 'কোন কমিউনিটি সদস্য পাওয়া যায়নি')
        }
      }
    } catch (err) {
      console.error('Failed to get community number:', err)
      setError(err.message)
    } finally {
      setWakeUpLoading(false)
    }
  }

  const handleStatusUpdate = async (trackingId, statusId, phoneNumber) => {
    try {
      // Use existing wake-up API to update status
      const result = await apiService.updateCallStatus(trackingId, statusId)
      console.log('Status updated:', statusId, 'for phone:', phoneNumber)
      
      // Update state based on response
      setCanRequestNext(result.can_request_next !== false) // Allow next for community
      setRemainingCalls(result.remaining_daily_calls || 5)
      
      // Don't automatically request next number - let user manually request it
      // This matches the behavior of general wake-up page
      
    } catch (err) {
      console.error('Failed to update status:', err)
      setError(err.message)
    }
  }

  const handleRequestNext = async () => {
    if (selectedCommunity) {
      await getNextCommunityNumberForCommunity(selectedCommunity)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading community data...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={logout} onAuth={openAuth} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold mb-2">কমিউনিটি জাগান</h1>
            <p className="text-muted-foreground">আপনার পরিবার, বন্ধু এবং পরিচিতদের জাগান</p>
          </div>

          {!selectedCommunity ? (
            /* Community Selection */
            <div>
              {communities.length === 0 ? (
                <Card className="text-center py-12">
                  <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">কোন কমিউনিটি নেই</h3>
                  <p className="text-muted-foreground mb-4">
                    কমিউনিটি জাগানোর জন্য প্রথমে একটি কমিউনিটি তৈরি করুন।
                  </p>
                  <Button onClick={() => navigate('/communities')}>
                    কমিউনিটি তৈরি করুন
                  </Button>
                </Card>
              ) : (
                <div>
                  <h2 className="text-xl font-semibold mb-4">আপনার কমিউনিটি নির্বাচন করুন:</h2>
                  <div className="space-y-4">
                    {communities.map((community) => (
                      <Card key={community.id} className="cursor-pointer hover:bg-accent transition-colors" onClick={() => selectCommunity(community.id)}>
                        <Card.Content className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold">{community.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {community.member_count} জন সদস্য
                              </p>
                            </div>
                            <Icon name="ChevronRight" className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </Card.Content>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Community Wake Up Interface */
            <div>
              {/* Community Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCommunity(null)
                        setAvailableMembers([])
                        setCurrentNumber(null)
                        setTrackingId(null)
                        activeRequestRef.current = false
                      }}
                    >
                      <Icon name="ArrowLeft" className="w-4 h-4 mr-1" />
                      ফিরে যান
                    </Button>
                    <div>
                      <h2 className="text-xl font-semibold">{selectedCommunity.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        {availableMembers.length} জন জাগানোর জন্য উপলব্ধ
                      </p>
                    </div>
                  </div>
                  
                  {/* See All Members Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.location.href = `/communities/${selectedCommunity.id}/members`
                    }}
                  >
                    <Icon name="Users" className="w-4 h-4 mr-2" />
                    সব সদস্য দেখুন
                  </Button>
                </div>
                
                {/* Current Time */}
                <div className="text-center text-sm text-muted-foreground">
                  বর্তমান সময়: {formatBengaliTime(currentTime.toLocaleTimeString('en-US', { hour12: true }))}
                </div>
              </div>

              {/* Wake-up Card */}
              {availableMembers.length === 0 || !currentNumber ? (
                <Card className="text-center py-12">
                  <Icon name="CheckCircle" className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-lg font-semibold mb-2">সবাই জেগে গেছে!</h3>
                  <p className="text-muted-foreground mb-4">
                    এই কমিউনিটির সবাই আজকে জেগে গেছে বা কল করা হয়েছে।
                  </p>
                  <Button onClick={() => {
                    setSelectedCommunity(null)
                    setAvailableMembers([])
                    setCurrentNumber(null)
                    setTrackingId(null)
                  }}>
                    অন্য কমিউনিটি নির্বাচন করুন
                  </Button>
                </Card>
              ) : (
                <WakeUpNumberCard
                  number={currentNumber}
                  trackingId={trackingId}
                  remainingCalls={remainingCalls}
                  maxCalls={5}
                  onStatusUpdate={handleStatusUpdate}
                  onRequestNext={handleRequestNext}
                  canRequestNext={canRequestNext}
                  loading={wakeUpLoading}
                  showUpdateButton={true}
                  rateLimited={false}
                  availableCount={availableMembers.length}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default CommunityWakeUp